import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import BarChartIcon from '@mui/icons-material/BarChart';
import PaperPlane from 'app/assets/icons/paper-plane.svg?react';
import PaperAirplaneIcon from 'app/assets/icons/paper-airplane-illustration.svg?react';
import { isOnboardingAcknowledged, acknowledgeOnboarding } from './onboarding-acknowledged';

const getMainEl = () => document.getElementById('fuse-main')!;

const steps = [
  {
    icon: (
      <Box className="w-30 h-30 rounded-8 flex items-center justify-center bg-featured">
        <AddIcon className="text-white text-[24px]" />
      </Box>
    ),
    label: 'Create',
    description: 'Craft your message and choose your audience in minutes.',
  },
  {
    icon: <PaperPlane className="w-30 h-30 text-featured" />,
    label: 'Send',
    description: 'Deliver your campaign at the perfect time with just a click.',
  },
  {
    icon: <BarChartIcon className="text-featured text-[30px]" />,
    label: 'Track',
    description: "See who engaged and measure your campaign's success easily.",
  },
];

export default function SmsCampaignsOnboarding() {
  const [isOpen, setIsOpen] = useState(!isOnboardingAcknowledged());
  const navigate = useNavigate();

  const handleClose = () => {
    acknowledgeOnboarding();
    setIsOpen(false);
  };

  const handleCreateCampaign = () => {
    handleClose();
    navigate('/sms-campaigns/new-campaign');
  };

  return (
    <Modal
      open={isOpen}
      container={getMainEl}
      slotProps={{
        root: {
          sx: { position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        backdrop: {
          sx: {
            position: 'absolute',
            backdropFilter: 'blur(3px)',
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
          },
        },
      }}
    >
      <Box
        className="bg-white rounded-12 flex flex-col gap-24 w-[800px]"
        sx={{
          boxShadow:
            '0 0 0.5px rgba(66,71,76,0.32), 0 4px 8px rgba(66,71,76,0.05), 0 4px 16px rgba(0,0,0,0.16)',
        }}
      >
        <Box className="flex items-center justify-between pl-24 pr-16 pt-18">
          <Typography variant="h6">SMS Campaigns</Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box className="flex flex-col items-center gap-8 mt-[20px]">
          <PaperAirplaneIcon className="shrink-0" style={{ width: 99, height: 96 }} />
          <Typography variant="h5">Reach Your Patients with SMS Campaigns</Typography>
          <Typography className="text-16 text-typography-primary">
            Easily connect, send, and monitor results in one place.
          </Typography>
        </Box>
        <Box className="flex mt-[20px]">
          {steps.map((step, index) => (
            <Box
              key={step.label}
              className="flex-1 flex flex-col items-center gap-8 px-24 py-16"
              sx={
                index < steps.length - 1
                  ? { borderRight: '1px solid', borderColor: 'divider' }
                  : undefined
              }
            >
              {step.icon}
              <Typography className="font-600 text-14">{step.label}</Typography>
              <Typography className="text-13 text-typography-secondary text-center">
                {step.description}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box className="flex items-center justify-end gap-12 px-24 pb-24 mt-[20px]">
          <Button
            variant="outlined"
            onClick={handleClose}
            className="border-1 border-solid border-typography-primary text-typography-primary rounded-8 normal-case"
          >
            Not Now
          </Button>
          <Button
            className="bg-featured hover:bg-featured-hover text-white rounded-8 normal-case"
            variant="contained"
            onClick={handleCreateCampaign}
          >
            Create new campaign
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}